import { defineComponent, onMounted, ref } from 'vue'
import { getPersonList } from '../manage/api'
import { getRecommendPersonList, saveRecommendPerson } from './api'

function unwrapPayload(res) {
  return res && res.data !== undefined ? res.data : res
}

function extractList(res) {
  const payload = unwrapPayload(res)
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.data)) return payload.data
  return []
}

function extractMeta(res) {
  const payload = unwrapPayload(res)
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { total: 0, page: 1, page_size: 20, total_pages: 1 }
  }
  const total = Number(payload.total || 0)
  const page = Number(payload.page || 1)
  const pageSize = Number(payload.page_size || payload.pageSize || 20)
  const totalPages = Number(
    payload.total_pages ||
      payload.totalPages ||
      (total && pageSize ? Math.ceil(total / pageSize) : 1)
  )
  return {
    total: Number.isFinite(total) ? total : 0,
    page: Number.isFinite(page) ? page : 1,
    page_size: Number.isFinite(pageSize) ? pageSize : 20,
    total_pages: Number.isFinite(totalPages) ? Math.max(1, totalPages) : 1
  }
}

function pickPerson(row) {
  return row && row.person && typeof row.person === 'object' ? row.person : {}
}

function pickPersonId(row) {
  if (!row || typeof row !== 'object') return ''
  const id = row.person_id ?? row.personId ?? pickPerson(row).id
  return id == null ? '' : String(id)
}

function pickPersonName(row) {
  const person = pickPerson(row)
  const name = person.name ?? person.real_name
  return name != null && String(name).trim() !== '' ? String(name) : '—'
}

function pickPersonAvatar(row) {
  const person = pickPerson(row)
  const avatar = person.avatar_url ?? person.avatarUrl
  return avatar != null && String(avatar).trim() !== '' ? String(avatar) : ''
}

function pickPersonPublic(row) {
  const person = pickPerson(row)
  const value = person.is_public ?? person.isPublic
  if (value === true || value === 1 || value === '1') return true
  if (value === false || value === 0 || value === '0') return false
  return null
}

function pickPublicLabel(row) {
  const value = pickPersonPublic(row)
  if (value === true) return '公开'
  if (value === false) return '非公开'
  return '—'
}

function pickStateLabel(state) {
  if (state === 1 || state === '1') return '推荐中'
  if (state === 0 || state === '0') return '已取消'
  return '—'
}

function formatDate(value) {
  if (!value) return '—'
  return String(value).replace('T', ' ').slice(0, 19)
}

export default defineComponent({
  name: 'RecommendPersonsView',
  setup() {
    const recommendList = ref([])
    const loading = ref(false)
    const saving = ref(false)

    const currentPage = ref(1)
    const pageSize = ref(20)
    const totalPages = ref(1)
    const total = ref(0)
    const filterState = ref('')

    const modalVisible = ref(false)
    const selectedPerson = ref(null)
    const personSearchQuery = ref('')
    const personSearchResults = ref([])
    const personSearchLoading = ref(false)
    const personDropdownOpen = ref(false)

    let personSearchTimer = null

    const fetchList = async () => {
      loading.value = true
      try {
        const params = {
          page: currentPage.value,
          page_size: pageSize.value
        }
        if (filterState.value !== '') params.state = Number(filterState.value)
        const response = await getRecommendPersonList(params)
        recommendList.value = extractList(response)
        const meta = extractMeta(response)
        currentPage.value = meta.page
        pageSize.value = meta.page_size
        totalPages.value = meta.total_pages
        total.value = meta.total
      } catch (error) {
        console.error('获取推荐纪念人列表失败:', error)
        recommendList.value = []
        alert(error.message || '获取推荐纪念人列表失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    const searchPersons = async () => {
      const keyword = String(personSearchQuery.value || '').trim()
      if (!keyword) {
        personSearchResults.value = []
        return
      }

      personSearchLoading.value = true
      try {
        const response = await getPersonList({
          page: 1,
          page_size: 20,
          search: keyword
        })
        personSearchResults.value = response.data?.data || []
      } catch (error) {
        console.error('搜索纪念人失败:', error)
        personSearchResults.value = []
      } finally {
        personSearchLoading.value = false
      }
    }

    const handlePersonSearch = () => {
      personDropdownOpen.value = true
      if (personSearchTimer) clearTimeout(personSearchTimer)
      personSearchTimer = setTimeout(searchPersons, 300)
    }

    const selectPerson = (person) => {
      selectedPerson.value = person
      personSearchQuery.value = person.name || ''
      personDropdownOpen.value = false
      personSearchResults.value = []
    }

    const clearSelectedPerson = () => {
      selectedPerson.value = null
      personSearchQuery.value = ''
      personSearchResults.value = []
    }

    const closePersonDropdown = () => {
      personDropdownOpen.value = false
    }

    const openCreateModal = () => {
      clearSelectedPerson()
      modalVisible.value = true
    }

    const closeModal = () => {
      if (saving.value) return
      modalVisible.value = false
      clearSelectedPerson()
    }

    const handleAddRecommend = async () => {
      const person = selectedPerson.value
      const personId = person?.id
      if (personId == null || personId === '') {
        alert('请选择纪念人')
        return
      }

      const isPublic = person.is_public ?? person.isPublic
      if (isPublic === false || isPublic === 0 || isPublic === '0') {
        alert('仅公开纪念人可被推荐，请先确认该纪念人已设为公开')
        return
      }

      saving.value = true
      try {
        await saveRecommendPerson({ person_id: Number(personId), state: 1 })
        alert('添加推荐成功')
        closeModal()
        await fetchList()
      } catch (error) {
        console.error('添加推荐纪念人失败:', error)
        alert(error.message || '添加推荐失败，请稍后重试')
      } finally {
        saving.value = false
      }
    }

    const handleToggleState = async (row, state) => {
      const personId = pickPersonId(row)
      if (!personId) {
        alert('缺少纪念人 ID')
        return
      }

      const actionText = state === 1 ? '重新推荐' : '取消推荐'
      const personName = pickPersonName(row)
      if (!confirm(`确定要${actionText}「${personName}」吗？`)) return

      if (state === 1 && pickPersonPublic(row) === false) {
        alert('仅公开纪念人可被推荐，请先确认该纪念人已设为公开')
        return
      }

      saving.value = true
      try {
        await saveRecommendPerson({ person_id: Number(personId), state })
        alert(`${actionText}成功`)
        await fetchList()
      } catch (error) {
        console.error(`${actionText}失败:`, error)
        alert(error.message || `${actionText}失败，请稍后重试`)
      } finally {
        saving.value = false
      }
    }

    const handlePageChange = (page) => {
      currentPage.value = page
      fetchList()
    }

    const handleStateFilterChange = () => {
      currentPage.value = 1
      fetchList()
    }

    const resetFilters = () => {
      filterState.value = ''
      currentPage.value = 1
      fetchList()
    }

    onMounted(fetchList)

    return {
      recommendList,
      loading,
      saving,
      currentPage,
      totalPages,
      total,
      filterState,
      modalVisible,
      selectedPerson,
      personSearchQuery,
      personSearchResults,
      personSearchLoading,
      personDropdownOpen,
      pickPersonId,
      pickPersonName,
      pickPersonAvatar,
      pickPublicLabel,
      pickStateLabel,
      formatDate,
      openCreateModal,
      closeModal,
      handlePersonSearch,
      selectPerson,
      clearSelectedPerson,
      closePersonDropdown,
      handleAddRecommend,
      handleToggleState,
      handlePageChange,
      handleStateFilterChange,
      resetFilters
    }
  }
})
